import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';

import PropTypes from 'prop-types';
import Permissions from '../../auth/Permissions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import WarningHeader from '../../common/text/WarningHeader';
import {
  getProjectBillingRates,
  getProjectBillingRateTemplates,
  createProjectBillingRates,
  updateProjectBillingRate,
  deleteProjectBillingRate,
} from '../../projectBillingRates/states/projectBillingRates.actions';
import { getUsers } from '../../users/state/users.actions';
import ProjectBillingTemplateAddEditDrawer from '../../settings/Customization/ProjectBillingTemplateAddEditDrawer';
import ProjectBillingRateList from './ProjectBillingRateList';
import ProjectBillingRateAddFromTemplateDrawer from './ProjectBillingRateAddFromTemplateDrawer';

const deleteModal = (name, onOk) => CustomConfirmModal({
  title: (
    <WarningHeader
      justify="center"
      title={`Delete billing rate “${name}”?`}
    />
  ),
  onOk,
});

export default function ProjectAddBillingRate({
  projectId,
  divisionId,
}) {
  const dispatch = useDispatch();
  const [showFormDrawer, setShowFormDrawer] = useState(false);
  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);
  const [selectedRate, setSelectedRate] = useState();

  const projectRatesMap = useSelector(
    (state) => state.projectBillingRates.projectRates?.[projectId],
  );

  const ratesList = Object.values(projectRatesMap ?? {});

  const canEdit = Permissions.has('PROJECTS_WRITE');

  useEffect(() => {
    if (!projectId) return;
    dispatch(getProjectBillingRates(projectId));
  }, [projectId]);

  useEffect(() => {
    dispatch(getProjectBillingRateTemplates());
    dispatch(getUsers());
  }, []);

  const closeFormDrawer = useCallback(() => {
    setSelectedRate();
    setShowFormDrawer(false);
  }, []);

  const onSubmitNew = useCallback(async (values) => {
    if (!projectId) return;
    const payload = {
      name: values.name?.trim(),
      type: values.type,
      regularBillingRate: values.regularBillingRate ?? null,
      otBillingRate: values.otBillingRate ?? null,
      dtBillingRate: values.dtBillingRate ?? null,
      regularCostRate: values.regularCostRate ?? null,
      otCostRate: values.otCostRate ?? null,
      dtCostRate: values.dtCostRate ?? null,
      hourlyBillingRate: values.hourlyBillingRate ?? null,
      dailyBillingRate: values.dailyBillingRate ?? null,
      weeklyBillingRate: values.weeklyBillingRate ?? null,
      monthlyBillingRate: values.monthlyBillingRate ?? null,
      hourlyCostRate: values.hourlyCostRate ?? null,
      dailyCostRate: values.dailyCostRate ?? null,
      weeklyCostRate: values.weeklyCostRate ?? null,
      monthlyCostRate: values.monthlyCostRate ?? null,
      userIds: values.userIds ?? [],
      positionIds: values.positionIds ?? [],
      equipmentTypeIds: values.equipmentTypeIds ?? [],
      equipmentIds: values.equipmentIds ?? [],
    };
    const result = selectedRate
      ? await dispatch(updateProjectBillingRate(projectId, selectedRate.id, payload))
      : await dispatch(createProjectBillingRates(projectId, [payload]));
    if (result?.success) {
      message.success('Billing rate saved successfully.');
      closeFormDrawer();
    } else {
      message.error(result?.message || 'Could not save billing rate.');
    }
  }, [closeFormDrawer, projectId, selectedRate]);

  const onAddNew = useCallback(() => {
    setSelectedRate();
    setShowFormDrawer(true);
  }, []);

  const onAddFromTemplate = useCallback(() => {
    setShowTemplateDrawer(true);
  }, []);

  const onSubmitFromTemplate = useCallback((payload) => {
    if (!projectId || !payload?.length) {
      return Promise.resolve({
        success: false,
        message: 'No billing rates to add.',
        invalidSourceTemplateIds: [],
      });
    }
    return dispatch(createProjectBillingRates(projectId, payload));
  }, [projectId]);

  const onEdit = useCallback((record) => {
    setSelectedRate(record);
    setShowFormDrawer(true);
  }, []);

  const onDelete = useCallback((record) => {
    if (!projectId) return;
    deleteModal(record.name, () => dispatch(deleteProjectBillingRate(projectId, record.id)));
  }, [projectId]);

  return (
    <div>
      <ProjectBillingRateList
        rates={ratesList}
        canEdit={canEdit}
        onAddFromTemplate={onAddFromTemplate}
        onAddNew={onAddNew}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      <ProjectBillingRateAddFromTemplateDrawer
        divisionId={divisionId}
        visible={showTemplateDrawer}
        onClose={() => setShowTemplateDrawer(false)}
        onSubmit={onSubmitFromTemplate}
      />
      <ProjectBillingTemplateAddEditDrawer
        selectedEntry={selectedRate}
        visible={showFormDrawer}
        toggle={closeFormDrawer}
        onSubmit={onSubmitNew}
        isFromTemplate={false}
        divisionId={divisionId}
        key={selectedRate?.id ?? 'new'}
      />
    </div>
  );
}

ProjectAddBillingRate.propTypes = {
  projectId: PropTypes.string.isRequired,
  divisionId: PropTypes.string.isRequired,
};
